// ============================================================================
// Mon Temps d'Étude — Banque fin CP (entrée CE1)
// Production conforme spec v2.1 ratifiée par Ana le 19/09/2026
// Worktree: feature/lumen-diag-bank-2026-09
// Auteur: Lumen MTE <lumen@mte.local>
// Date production: 2026-09-19
// ============================================================================
//
// NOTE SCHÉMA: ce fichier étend le type union `BanqueItem` runtime pour
// supporter schoolLevel='CP'. La définition runtime actuelle dans
// mte-shell/src/lib/banque-types.ts doit être élargie avant intégration.
// Lumen fournit le contenu pédagogique; Atelier adapte le typage.
//
// DOMAINES COUVERTS (10): lecture_code · fluence · compréhension · vocabulaire
//   · écriture_orthographe · consignes · numération · calcul_mental · opérations · problèmes
//
// AUDIO (per Ana-direct 22:50): instruction_audio distinct de stimulus_audio
//   → indiqués via `consigne` ou `meta.audio` selon archétype runtime.
//
// LECTUREVOIXHAUTE — 3 usages (per Ana-direct 19/09 ~13:00):
//   lecture_debutante | entrainement_fluence | evaluation_1_minute
//   → encodé via `meta.texte_usage` + `meta.preparation`.
//
// JURIDIQUE TEXTES (per Ana-direct 23:00):
//   mte_propre > domaine_public_CONFIRME > licence_COMPATIBLE
//   PAS automatiquement autorisé: usage pédagogique · MENJ · courte citation · gratuit
// ============================================================================

import type { BanqueItem } from '../lib/banque-types';

type CPItem = BanqueItem & { schoolLevel: 'CP' };

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 1 · lecture_code (FR) — décodage, syllabes, sons
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_LECTURE_CODE: CPItem[] = [
  {
    id: 'FR-CP-LEC-01',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-LEC-01',
    enonce: "Quel son entends-tu dans le mot 'chat' ?",
    options: ['[a]', '[é]', '[i]', '[o]'],
    bonneReponse: '[a]',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · correspondances grapho-phonémiques',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-02',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'FR-CP-LEC-02',
    enonce: "Combien de syllabes a le mot 'papillon' ? (réponse en chiffre)",
    bonneReponse: '3',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · découpage syllabique',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-03',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-LEC-03',
    enonce: "Quel son entends-tu au début du mot 'lion' ?",
    options: ['[l]', '[j]', '[i]', '[n]'],
    bonneReponse: '[j]',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · sons complexes',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-04',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-LEC-04',
    enonce: "Quel son fait le 'eau' dans 'gâteau' ?",
    options: ['[a]', '[é]', '[o]', '[u]'],
    bonneReponse: '[o]',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · graphèmes complexes',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-05',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-LEC-05',
    enonce: "Trouve le mot qui commence par le même son que 'pain'.",
    options: ['papa', 'main', 'lune', 'jardin'],
    bonneReponse: 'papa',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · discrimination auditive',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-06',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-LEC-06',
    enonce: "Quel mot se termine par le son [o] ?",
    options: ['voiture', 'maison', 'cahier', 'cartable'],
    bonneReponse: 'voiture',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · position du son',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-07',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'FR-CP-LEC-07',
    enonce: "Combien de syllabes a le mot 'électricien' ? (réponse en chiffre)",
    bonneReponse: '5',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · mots longs',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-LEC-08',
    schoolLevel: 'CP',
    archetype: 'dictee',
    competence: 'FR-CP-LEC-08',
    enonce: 'Écris le mot que tu entends.',
    audioUrl: '/assets/phonemes/cp/table.wav',
    bonneReponse: 'table',
    replayMax: 3,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · dictée de mots outils',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: true, type: 'sound_discrimination' },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 2 · fluence (FR) — 3 USAGES (per Ana-direct 19/09 ~13:00)
// Seuils CP: ≥ 30 sans préparation / ≥ 50 avec préparation
// Source: DEPP NI 2026-31
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_FLUENCE: CPItem[] = [
  // ── USAGE lecture_debutante · 5-15 mots · difficulté 1 ──
  {
    id: 'FR-CP-FLU-01',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-01',
    enonce: 'Lis ce mini-texte à voix haute.',
    textALire: 'Le chat dort.',
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'lecture_debutante',
      titre: 'Le chat',
      mots_total: 4,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'DEPP NI 2026-31 · CP · fluence · lecture_debutante',
      preuve_url: 'https://www.education.gouv.fr/sites/default/files/document/deppni-2026-31fluence-de-lecturepdf-518870.pdf',
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-FLU-02',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-02',
    enonce: 'Lis cette petite phrase à voix haute.',
    textALire: 'La rose est belle.',
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'lecture_debutante',
      titre: 'La rose',
      mots_total: 5,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'DEPP NI 2026-31 · CP · fluence · lecture_debutante',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-FLU-03',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-03',
    enonce: 'Lis ce mini-texte à voix haute.',
    textALire: 'Mon chien joue dans le jardin.',
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'lecture_debutante',
      titre: 'Le petit chien',
      mots_total: 8,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'DEPP NI 2026-31 · CP · fluence · lecture_debutante',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  // ── USAGE entrainement_fluence · 15-50 mots · difficulté 2-4 ──
  {
    id: 'FR-CP-FLU-04',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-04',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: 'Le petit chat regarde les oiseaux sur le mur. Il veut jouer avec eux.',
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'entrainement_fluence',
      titre: 'Le petit chat curieux',
      mots_total: 19,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO cycle 2 · CP · Lecture · fluence · entrainement progressif',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-FLU-05',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-05',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "La classe va à la mer. Les enfants emportent leur maillot, leur serviette et leur chapeau. Le car est grand. Il s'arrête sur la plage. Tout le monde court vers le sable.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'entrainement_fluence',
      titre: "Une journée à la mer",
      mots_total: 42,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO cycle 2 · CP · Lecture · fluence · entrainement progressif',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-FLU-06',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-06',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: 'Le matin, maman va au marché. Elle achète des tomates, des carottes, des pommes et du pain. Le marchand sourit. Il pèse les légumes sur sa grande balance. À la fin, maman donne les courses à papa qui les range dans le panier.',
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'entrainement_fluence',
      titre: 'Au marché',
      mots_total: 56,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO cycle 2 · CP · Lecture · fluence · entrainement progressif',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  // ── USAGE evaluation_1_minute · ≥ 60 mots · difficulté 4-5 · FORMEL ──
  {
    id: 'FR-CP-FLU-07',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-07',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Aujourd'hui c'est la fête de l'école. Tous les enfants sont contents. Ils ont préparé des chansons et des poèmes. Le directeur parle au micro. Les parents applaudissent. Ensuite, tout le monde va dans la cour pour manger des gateaux. Il fait très beau dehors. C'est une belle journée.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      preparation: 'avec',
      titre: "La fête de l'école",
      mots_total: 75,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'DEPP NI 2026-31 · CP · fluence · evaluation formelle · ≥ 50 mots/min apres preparation',
      preuve_url: 'https://www.education.gouv.fr/sites/default/files/document/deppni-2026-31fluence-de-lecturepdf-518870.pdf',
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-FLU-08',
    schoolLevel: 'CP',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CP-FLU-08',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Tom est un petit garçon qui aime les fleurs. Tous les jours, il arrose son jardin. Il a planté des tulipes, des jonquilles et des marguerites. Il parle à ses fleurs. Il leur dit bonjour le matin et bonne nuit le soir. Quand les fleurs sont grandes, Tom les montre à son papa qui est très fier de lui.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      preparation: 'sans',
      titre: 'Le petit jardinier',
      mots_total: 79,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'DEPP NI 2026-31 · CP · fluence · evaluation formelle · ≥ 30 mots/min sans preparation',
      preuve_url: 'https://www.education.gouv.fr/sites/default/files/document/deppni-2026-31fluence-de-lecturepdf-518870.pdf',
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 3 · compréhension (FR)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_COMPREHENSION: CPItem[] = [
  {
    id: 'FR-CP-COMP-01',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-COMP-01',
    enonce: "Lis ce texte : 'Le chien court dans le parc. Il voit un ballon rouge. Il attrape le ballon avec sa patte.' Qui attrape le ballon ?",
    options: ['Le chat', 'Le chien', 'Le ballon', 'Le parc'],
    bonneReponse: 'Le chien',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · compréhension · questions littérales',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-COMP-02',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-COMP-02',
    enonce: "Lis : 'Maman prépare un gâteau. Elle utilise de la farine, du sucre et des œufs. Le gâteau sent très bon.' Quels ingrédients ?",
    options: ['Du sel et du lait', 'De la farine, du sucre et des œufs', 'Du chocolat', 'Du pain'],
    bonneReponse: 'De la farine, du sucre et des œufs',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · compréhension · prélever informations',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-COMP-03',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-COMP-03',
    enonce: "Lis : 'Léa aime lire. Chaque soir, elle lit une histoire à sa petite sœur. Sa sœur écoute attentivement.' Que fait Léa le soir ?",
    options: ['Elle dort', 'Elle cuisine', 'Elle lit une histoire', 'Elle dessine'],
    bonneReponse: 'Elle lit une histoire',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · compréhension · qui fait quoi',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-COMP-04',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-COMP-04',
    enonce: "Lis : 'Pierre cherche son chien. Il l'appelle partout. Il ne le trouve pas. Soudain, il voit ses petites pattes sous la table. Le chien dort.' Où est le chien ?",
    options: ['Dans le jardin', 'Sous la table', 'Sur le lit', 'Dans la cuisine'],
    bonneReponse: 'Sous la table',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · compréhension · localisation',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-COMP-05',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'FR-CP-COMP-05',
    enonce: "Lis : 'Le soleil brille. Les oiseaux chantent. Les enfants jouent dans le jardin.' Quel temps fait-il ?",
    consigne: 'Réponds en un seul mot (sans déterminant, en minuscules).',
    bonneReponse: 'beau',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · compréhension · restitution mot-clé',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-COMP-06',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-COMP-06',
    enonce: "Lis : 'Marc a perdu sa balle. Il la cherche dans toute la maison. Il regarde sous le lit, dans la cuisine, derrière le canapé. Enfin, il la trouve dans le jardin.' Où Marc trouve-t-il sa balle ?",
    options: ['Sous le lit', 'Dans la cuisine', 'Derrière le canapé', 'Dans le jardin'],
    bonneReponse: 'Dans le jardin',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Lecture · compréhension · information explicite',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 4 · vocabulaire (FR)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_VOCABULAIRE: CPItem[] = [
  {
    id: 'FR-CP-VOC-01',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-VOC-01',
    enonce: "Quel mot veut dire la même chose que 'content' ?",
    options: ['triste', 'joyeux', 'fatigué', 'fâché'],
    bonneReponse: 'joyeux',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Vocabulaire · synonymes simples',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-VOC-02',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-VOC-02',
    enonce: "Quel mot veut dire le contraire de 'grand' ?",
    options: ['haut', 'gros', 'petit', 'long'],
    bonneReponse: 'petit',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Vocabulaire · antonymes',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-VOC-03',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-VOC-03',
    enonce: "Quel mot appartient à la famille de 'fleur' ?",
    options: ['fleuve', 'fleurir', 'flèche', 'forêt'],
    bonneReponse: 'fleurir',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Vocabulaire · familles de mots',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-VOC-04',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-VOC-04',
    enonce: "Quel mot désigne un animal ?",
    options: ['table', 'souris', 'livre', 'pomme'],
    bonneReponse: 'souris',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Vocabulaire · catégorisation',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-VOC-05',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-VOC-05',
    enonce: "Quel mot est un verbe ?",
    options: ['maison', 'manger', 'bleu', 'vite'],
    bonneReponse: 'manger',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Vocabulaire · classes grammaticales initiation',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-VOC-06',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'FR-CP-VOC-06',
    enonce: "Trouve un mot de la famille de 'dent'. Réponse en un mot.",
    bonneReponse: 'dentiste',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Vocabulaire · dérivation',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 5 · écriture_orthographe (FR)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_ECRITURE_ORTHOGRAPHE: CPItem[] = [
  {
    id: 'FR-CP-ORTH-01',
    schoolLevel: 'CP',
    archetype: 'dictee',
    competence: 'FR-CP-ORTH-01',
    enonce: 'Écris le mot que tu entends.',
    audioUrl: '/assets/phonemes/cp/chat.wav',
    bonneReponse: 'chat',
    replayMax: 3,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · dictée de mots outils',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: true, type: 'sound_discrimination' },
    },
  },
  {
    id: 'FR-CP-ORTH-02',
    schoolLevel: 'CP',
    archetype: 'dictee',
    competence: 'FR-CP-ORTH-02',
    enonce: 'Écris le mot que tu entends.',
    audioUrl: '/assets/phonemes/cp/papa.wav',
    bonneReponse: 'papa',
    replayMax: 3,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · dictée de mots outils',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: true, type: 'sound_discrimination' },
    },
  },
  {
    id: 'FR-CP-ORTH-03',
    schoolLevel: 'CP',
    archetype: 'dictee',
    competence: 'FR-CP-ORTH-03',
    enonce: 'Écris la phrase que tu entends.',
    audioUrl: '/assets/dictee/cp/il-fait-beau.wav',
    bonneReponse: 'Il fait beau.',
    replayMax: 3,
    evalMode: 'HUMAN',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · dictée de phrases courtes',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: true, type: 'sound_discrimination' },
    },
  },
  {
    id: 'FR-CP-ORTH-04',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'FR-CP-ORTH-04',
    enonce: "Écris le mot 'éléphant'.",
    bonneReponse: 'éléphant',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · copie de mots',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-ORTH-05',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'FR-CP-ORTH-05',
    enonce: "Écris le mot 'bonjour' en une seule réponse.",
    bonneReponse: 'bonjour',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · mémorisation orthographique',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-ORTH-06',
    schoolLevel: 'CP',
    archetype: 'production-ecrite',
    competence: 'FR-CP-ORTH-06',
    enonce: 'Écris une phrase avec le mot "maison".',
    minWords: 3,
    evalMode: 'HUMAN',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Écriture · production de phrases',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 6 · consignes (transverse)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_CONSIGNES: CPItem[] = [
  {
    id: 'FR-CP-CONS-01',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-CONS-01',
    enonce: 'Consigne : "Entoure les carrés rouges". Que dois-tu faire ?',
    options: ['Compter les ronds', 'Entourer les carrés rouges', 'Dessiner un carré', 'Effacer les ronds'],
    bonneReponse: 'Entourer les carrés rouges',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Comprendre une consigne 2 temps',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-CONS-02',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-CONS-02',
    enonce: 'Consigne : "Colorie le plus grand cercle". Que fais-tu ?',
    options: ['Colorier tous les cercles', 'Colorier le plus petit cercle', 'Colorier le plus grand cercle', 'Entourer les cercles'],
    bonneReponse: 'Colorier le plus grand cercle',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Comprendre une consigne avec notion de grandeur',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-CONS-03',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-CONS-03',
    enonce: 'Consigne : "Compte les triangles et écris le nombre". Que fais-tu en premier ?',
    options: ['Écrire le nombre', 'Compter les triangles', 'Dessiner des triangles', 'Effacer les triangles'],
    bonneReponse: 'Compter les triangles',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Comprendre une consigne 2 temps ordonnée',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-CONS-04',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-CONS-04',
    enonce: 'Consigne : "Entoure les fruits". Que dois-tu entourer ?',
    options: ['Les légumes', 'Les fruits', 'Les animaux', 'Les meubles'],
    bonneReponse: 'Les fruits',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Comprendre une consigne avec catégorisation',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CP-CONS-05',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'FR-CP-CONS-05',
    enonce: 'Consigne : "Place le jeton sur la première case". Où vas-tu ?',
    options: ['Sur la dernière case', 'Sur la première case', 'Entre les cases', 'Sous les cases'],
    bonneReponse: 'Sur la première case',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Comprendre une consigne avec position',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 7 · numération (MA) — jusqu'à 100
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_NUMERATION: CPItem[] = [
  {
    id: 'MA-CP-NB-01',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'MA-CP-NB-01',
    enonce: 'Quel nombre est le plus grand ?',
    options: ['23', '37', '19', '42'],
    bonneReponse: '42',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CP · Mathématiques · Nombres · comparer jusqu'à 100",
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-NB-02',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'MA-CP-NB-02',
    enonce: 'Range ces nombres du plus petit au plus grand. Quel est le nombre du milieu ?',
    options: ['18', '23', '28', '33'],
    bonneReponse: '23',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CP · Mathématiques · ranger jusqu'à 100",
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-NB-03',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-NB-03',
    enonce: 'Écris le nombre soixante-douze en chiffres.',
    bonneReponse: '72',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CP · Mathématiques · lire/écrire jusqu'à 100",
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-NB-04',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'MA-CP-NB-04',
    enonce: 'Combien de dizaines y a-t-il dans le nombre 47 ?',
    options: ['7', '4', '47', '44'],
    bonneReponse: '4',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · décomposer',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-NB-05',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'MA-CP-NB-05',
    enonce: "Quel nombre est entre 49 et 51 ?",
    options: ['48', '49', '50', '52'],
    bonneReponse: '50',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · encadrer',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-NB-06',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-NB-06',
    enonce: 'Complète : 65 = 60 + ___. Quel est le nombre manquant ?',
    bonneReponse: '5',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · décomposition dizaines et unités',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 8 · calcul_mental (MA)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_CALCUL_MENTAL: CPItem[] = [
  {
    id: 'MA-CP-CAL-01',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-CAL-01',
    enonce: 'Combien font 5 + 3 ?',
    bonneReponse: '8',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · Calcul mental',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-CAL-02',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-CAL-02',
    enonce: 'Combien font 7 + 5 ?',
    bonneReponse: '12',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CP · Mathématiques · tables d'addition",
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-CAL-03',
    schoolLevel: 'CP',
    archetype: 'vf',
    competence: 'MA-CP-CAL-03',
    enonce: '6 + 6 = 12. Vrai ou faux ?',
    bonneReponse: true,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · doubles',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-CAL-04',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-CAL-04',
    enonce: 'Combien font 9 + 4 ?',
    bonneReponse: '13',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · addition avec retenue',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-CAL-05',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-CAL-05',
    enonce: 'Combien font le double de 8 ?',
    bonneReponse: '16',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · doubles',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-CAL-06',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'MA-CP-CAL-06',
    enonce: 'Combien font 8 - 3 ?',
    options: ['4', '5', '6', '11'],
    bonneReponse: '5',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · soustraction simple',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 9 · opérations (MA)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_OPERATIONS: CPItem[] = [
  {
    id: 'MA-CP-OP-01',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-OP-01',
    enonce: "Pose l'opération 23 + 14. Quel est le résultat ?",
    bonneReponse: '37',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · addition posée sans retenue',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-OP-02',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-OP-02',
    enonce: "Pose l'opération 35 + 22. Quel est le résultat ?",
    bonneReponse: '57',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · addition posée sans retenue',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-OP-03',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-OP-03',
    enonce: "Pose l'opération 41 + 26 en colonnes. Quel est le résultat ?",
    bonneReponse: '67',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · addition posée sans retenue',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-OP-04',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-OP-04',
    enonce: "Pose l'opération 54 + 13. Quel est le résultat ?",
    bonneReponse: '67',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · addition posée sans retenue',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// DOMAINE 10 · problèmes (MA)
// ────────────────────────────────────────────────────────────────────────────

const ITEMS_PROBLEMES: CPItem[] = [
  {
    id: 'MA-CP-PROB-01',
    schoolLevel: 'CP',
    archetype: 'texte-a-trou',
    competence: 'MA-CP-PROB-01',
    enonce: "Lucas a 5 billes. Il en gagne 3. Combien a-t-il maintenant ? Réponse : ___ billes.",
    enonceSegments: ["Lucas a 5 billes. Il en gagne 3. Combien a-t-il maintenant ? Réponse : ", "{T1}", " billes."],
    trous: [{ id: 'T1', bonneReponse: '8', tolerance: 0 }],
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · Résolution · une étape',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-PROB-02',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-PROB-02',
    enonce: "Emma a 12 images. Elle en donne 4 à son frère. Combien lui en reste-t-il ?",
    bonneReponse: '8',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · Résolution · une étape',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-PROB-03',
    schoolLevel: 'CP',
    archetype: 'qcm',
    competence: 'MA-CP-PROB-03',
    enonce: "Dans un panier, il y a 6 pommes rouges et 4 pommes vertes. Combien de pommes en tout ?",
    options: ['2', '6', '10', '4'],
    bonneReponse: '10',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · Résolution · recherche du tout',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-PROB-04',
    schoolLevel: 'CP',
    archetype: 'saisie',
    competence: 'MA-CP-PROB-04',
    enonce: "Un jardinier a planté 7 fleurs le matin et 5 l'après-midi. Combien en tout ?",
    bonneReponse: '12',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · Résolution · une étape',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CP-PROB-05',
    schoolLevel: 'CP',
    archetype: 'texte-a-trou',
    competence: 'MA-CP-PROB-05',
    enonce: "Julie avait 15 bonbons. Elle en a mangé 6. Il lui reste ___ bonbons.",
    enonceSegments: ["Julie avait 15 bonbons. Elle en a mangé 6. Il lui reste ", "{T1}", " bonbons."],
    trous: [{ id: 'T1', bonneReponse: '9', tolerance: 0 }],
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CP · Mathématiques · Résolution · une étape',
      preuve_url: null,
      instruction_audio: { cp: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

// ────────────────────────────────────────────────────────────────────────────
// AGGREGATION FINALE
// ────────────────────────────────────────────────────────────────────────────

export const BANQUE_CP: CPItem[] = [
  ...ITEMS_LECTURE_CODE,
  ...ITEMS_FLUENCE,
  ...ITEMS_COMPREHENSION,
  ...ITEMS_VOCABULAIRE,
  ...ITEMS_ECRITURE_ORTHOGRAPHE,
  ...ITEMS_CONSIGNES,
  ...ITEMS_NUMERATION,
  ...ITEMS_CALCUL_MENTAL,
  ...ITEMS_OPERATIONS,
  ...ITEMS_PROBLEMES,
];

export const BANQUE_CP_COUNT = BANQUE_CP.length;
export const BANQUE_CP_REPARTITION = {
  lecture_code: ITEMS_LECTURE_CODE.length,
  fluence: ITEMS_FLUENCE.length,
  comprehension: ITEMS_COMPREHENSION.length,
  vocabulaire: ITEMS_VOCABULAIRE.length,
  ecriture_orthographe: ITEMS_ECRITURE_ORTHOGRAPHE.length,
  consignes: ITEMS_CONSIGNES.length,
  numeration: ITEMS_NUMERATION.length,
  calcul_mental: ITEMS_CALCUL_MENTAL.length,
  operations: ITEMS_OPERATIONS.length,
  problemes: ITEMS_PROBLEMES.length,
};
